import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Building,
  Phone,
  CircleDot,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "bootstrap-icons/font/bootstrap-icons.css";
import MapWrapperBranch from "../../../../Components/UiComponents/MapWrapper/MapWrapperBranch";

// Component for displaying Branch details in view page
const ViewBranchDetails = () => {
  const location = useLocation();

  const initialBranchData =
    location.state?.data ||
    JSON.parse(sessionStorage.getItem("BranchData") || "{}");

  // Normalizes and standardizes branch data structure from different sources
  const normalizeBranchData = (data) => {
    if (!data) {
      return {};
    }

    if (data.bankEntity && data.branchDetailsList) {
      return {
        ...data.bankEntity,
        branchDetailsList: data.branchDetailsList || [],
        parentBank: data.bankEntity.bankName || "N/A",
        branchName: data.bankEntity.bankName || "N/A",
        branchType: determineBranchType(data.bankEntity),
      };
    } else if (data.bankName) {
      return {
        ...data,
        branchDetailsList: data.branchDetailsList || [],
        parentBank: data.bankName || "N/A",
        branchName: data.branchName || data.bankName || "N/A",
        branchType: determineBranchType(data),
      };
    }
    return data;
  };

  // Determines the branch type from branch details, defaults to "Regular"
  const determineBranchType = (branchDetail) => {
    if (!branchDetail) return "Regular";
    return branchDetail.branchType || "Regular";
  };

  const branchData = normalizeBranchData(initialBranchData);

  useEffect(() => {
    if (location.state?.data) {
      sessionStorage.setItem("BranchData", JSON.stringify(location.state.data));
    }
  }, [location.state?.data]);

  // Returns provided data or "N/A" if data is null/undefined
  const displayData = (data) => data || "N/A";

  // Converts 24-hour time format to 12-hour format with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return "N/A";

    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;

    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const [selectedMapOption, setSelectedMapOption] = useState("CC Camera");

  const mapTitle = "Bank & Branch Location";

  const mapCoordinates = [
    {
      x: branchData?.latitude || 25.4223,
      y: branchData?.longitude || 86.1391,
      locationId: "main-bank",
      locationType: "Bank",
      popupdata: {
        title: branchData?.bankName,
        description: branchData?.addressLine1,
        status: branchData?.activeStatus ? "Active" : "Inactive",
        type: "Bank",
        contact: branchData?.phoneNumber,
        email: branchData?.email,
      },
    },
    ...(branchData.branchDetailsList || []).map((branch, index) => ({
      x: branch.latitude || 25.4223 + Math.random() * 0.05,
      y: branch.longitude || 86.1391 + Math.random() * 0.05,
      locationId: `branch-${index}`,
      locationType: "Branch",
      popupdata: {
        title: branch.branchName || "Branch Office",
        description: branch.addressLine1 || "N/A",
        status: branch.activeStatus ? "Active" : "Inactive",
        type: "Branch",
        contact: branch.phoneNumber || "N/A",
        email: branch.email || "N/A",
      },
    })),
  ];

  // Handles map option selection changes
  const handleMapOptionChange = (e) => {
    setSelectedMapOption(e.target.value);
  };

  // Logs selected coordinates from map interaction
  const handleChooseCoordinates = (longitude, latitude) => {
    console.log(`Selected coordinates: ${longitude}, ${latitude}`);
  };

  const [expandedBranches, setExpandedBranches] = useState({});

  // Toggles the expansion state of a specific branch card
  const toggleBranchExpansion = (branchIndex) => {
    setExpandedBranches((prev) => ({
      ...prev,
      [branchIndex]: !prev[branchIndex],
    }));
  };

  return (
    <div className="container-fluid p-0" style={{ overflow: "hidden" }}>
      <div className="row g-0">
        <div className="col-md-12 p-3 bg-white">
          <div
            className="card-header"
            style={{
              backgroundColor: "#1a7a73",
              color: "white",
              marginTop: "-20px",
            }}
          >
            <h5 className="card-title mb-0">
              Bank Details: {displayData(branchData?.bankName)}
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-8">
                <div className="card border-0 mb-3">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <Building
                        size={18}
                        className="me-2"
                        style={{ color: "black" }}
                      />
                      <h6 className="mb-0" style={{ color: "black" }}>
                        Bank Details
                      </h6>
                    </div>
                  </div>
                  <div className="card-body">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "20px",
                        marginBottom: "20px",
                      }}
                    >
                      {/* First column */}
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Bank Name
                        </p>
                        <p
                          className="mb-0"
                          style={{ fontSize: "15px", fontWeight: "500" }}
                        >
                          {displayData(branchData?.bankName)}
                        </p>
                      </div>
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          IFSC Code
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.ifscCode)}
                        </p>
                      </div>
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Mandal
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.mandal)}
                        </p>
                      </div>

                      {/* Second column */}
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          City
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.city)}
                        </p>
                      </div>
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          District
                        </p>
                        <div className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.district)}
                        </div>
                      </div>

                      {/* Third column */}
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Pincode
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.pinCode)}
                        </p>
                      </div>
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Contact Name
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.contactName)}
                        </p>
                      </div>

                      {/* Fourth column */}
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Mobile/Landline Number
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.phoneNumber)}
                        </p>
                      </div>
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Active status
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          <span
                            className={`badge ${
                              branchData?.activeStatus
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                          >
                            {branchData?.activeStatus ? "Active" : "Inactive"}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Email Address
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.email)}
                        </p>
                      </div>

                      {/* Fifth column */}
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Bank Type
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.branchType)}
                        </p>
                      </div>
                      <div>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Created on
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {branchData?.created_on
                            ? new Date(branchData.created_on)
                                .toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })
                                .replace(/\//g, "-") +
                              " " +
                              new Date(
                                branchData.created_on
                              ).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/A"}
                        </p>
                      </div>
                      <div style={{ gridColumn: "span 3" }}>
                        <p
                          className="mb-1 small"
                          style={{ color: "#FFA100", fontSize: "13px" }}
                        >
                          Address
                        </p>
                        <p className="mb-0" style={{ fontSize: "15px" }}>
                          {displayData(branchData?.addressLine1)}
                          {branchData?.addressLine2 && (
                            <span>, {branchData.addressLine2}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 mb-3">
                  <div
                    className="card-header"
                    style={{
                      backgroundColor: "#1a7a73",
                      color: "white",
                      marginTop: "-30px",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="bg-light p-2 mb-2 d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                          <CircleDot size={16} className="me-2" color="black" />
                          <h6 className="mb-0" style={{ color: "black" }}>
                            Branch Details
                          </h6>
                        </div>
                        <span className="badge bg-light text-dark">
                          {branchData.branchDetailsList
                            ? branchData.branchDetailsList.length
                            : 0}{" "}
                          Branches
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {!branchData.branchDetailsList ||
                    branchData.branchDetailsList.length === 0 ? (
                      <div className="alert alert-info">
                        <p className="mb-0">
                          No branch details available for this bank.
                        </p>
                      </div>
                    ) : (
                      <div
                        id="branchList"
                        style={{
                          maxHeight:
                            branchData.branchDetailsList.length > 1
                              ? "500px"
                              : "auto",
                          overflowY:
                            branchData.branchDetailsList.length > 1
                              ? "auto"
                              : "visible",
                        }}
                      >
                        {branchData.branchDetailsList.map((branch, index) => {
                          const workingHours =
                            branch.branch_work_time &&
                            branch.branch_work_time.length > 0
                              ? branch.branch_work_time
                                  .map(
                                    (wt) =>
                                      `${wt.day}: ${formatTime(
                                        wt.fromTime
                                      )} - ${formatTime(wt.toTime)}`
                                  )
                                  .join(", ")
                              : "Not specified";

                          const isExpanded = !!expandedBranches[index];

                          return (
                            <div className="card mb-3" key={`branch-${index}`}>
                              <div
                                className="card-header bg-light cursor-pointer"
                                onClick={() => toggleBranchExpansion(index)}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center">
                                    <h6
                                      className="mb-0"
                                      style={{ color: "black" }}
                                    >
                                      {branch.branchName || "Unnamed Branch"}
                                    </h6>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp size={18} color="#1a7a73" />
                                  ) : (
                                    <ChevronDown size={18} color="#1a7a73" />
                                  )}
                                </div>
                              </div>

                              {isExpanded && (
                                <div className="card-body">
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(3, 1fr)",
                                      gap: "20px",
                                      marginBottom: "20px",
                                    }}
                                  >
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Branch Name
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{
                                          fontSize: "15px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        {branch.branchName || "Unnamed Branch"}
                                      </p>
                                    </div>
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        IFSC Code
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.ifscCode || "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Mandal
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.mandal || "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        City
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.city || "N/A"}
                                      </p>
                                    </div>

                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        District
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.district || "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Pincode
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.pinCode || "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Phone Number
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.phoneNumber || "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Email
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.email || "N/A"}
                                      </p>
                                    </div>

                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Active Status
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        <span
                                          className={`badge ${
                                            branch.activeStatus
                                              ? "bg-success"
                                              : "bg-danger"
                                          }`}
                                        >
                                          {branch.activeStatus
                                            ? "Active"
                                            : "Inactive"}
                                        </span>
                                      </p>
                                    </div>
                                    <div>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Created On
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.created_on
                                          ? new Date(branch.created_on)
                                              .toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                              })
                                              .replace(/\//g, "-") +
                                            " " +
                                            new Date(
                                              branch.created_on
                                            ).toLocaleTimeString("en-US", {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                          : "N/A"}
                                      </p>
                                    </div>
                                    <div style={{ gridColumn: "span 3" }}>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Address
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {branch.addressLine1 || "N/A"}
                                        {branch.addressLine2
                                          ? `, ${branch.addressLine2}`
                                          : ""}
                                      </p>
                                    </div>
                                    <div style={{ gridColumn: "span 3" }}>
                                      <p
                                        className="mb-1 small"
                                        style={{
                                          color: "#FFA100",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Working Hours
                                      </p>
                                      <p
                                        className="mb-0"
                                        style={{ fontSize: "15px" }}
                                      >
                                        {workingHours}
                                      </p>
                                    </div>
                                  </div>

                                  {branch.branch_contact &&
                                    branch.branch_contact.length > 0 && (
                                      <div className="mt-3">
                                        <div className="d-flex align-items-center mb-2">
                                          <Phone
                                            size={16}
                                            className="me-2"
                                            color="#FFA100"
                                          />
                                          <p
                                            className="mb-0 small"
                                            style={{
                                              color: "#FFA100",
                                              fontSize: "13px",
                                            }}
                                          >
                                            Branch Contacts
                                          </p>
                                        </div>
                                        <div className="table-responsive">
                                          <table className="table table-sm table-bordered">
                                            <thead
                                              style={{
                                                backgroundColor: "#1a7a73",
                                                color: "white",
                                              }}
                                            >
                                              <tr>
                                                <th>Name</th>
                                                <th>Phone Number</th>
                                                <th>Email</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {branch.branch_contact.map(
                                                (contact, contactIndex) => (
                                                  <tr key={contactIndex}>
                                                    <td>{`${
                                                      contact.firstName || ""
                                                    } ${
                                                      contact.lastName || ""
                                                    }`}</td>
                                                    <td>
                                                      {contact.phoneNumber ||
                                                        "N/A"}
                                                    </td>
                                                    <td>
                                                      {contact.email || "N/A"}
                                                    </td>
                                                  </tr>
                                                )
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card-body p-0">
                  <div className="h-100" style={{ overflow: "hidden" }}>
                    <MapWrapperBranch
                      title={mapTitle}
                      selectedOption={selectedMapOption}
                      onChangeHandler={handleMapOptionChange}
                      containerStyles={{
                        height: "105vh",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      headerStyles={{
                        backgroundColor: "#1a7a73",
                        color: "white",
                        padding: "8px 16px",
                        display: "none",
                        height: "48px",
                      }}
                      bodyStyles={{
                        flex: 1,
                        overflow: "hidden",
                      }}
                      coordinates={mapCoordinates}
                      chooseCoordinates={handleChooseCoordinates}
                      zoom={13}
                      popUpShow={true}
                      showBranchMarkers={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBranchDetails;
